import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, ArrowSquareOut, Compass } from "@phosphor-icons/react";

/**
 * PropertyStreetViewCard — visual identification header for a property.
 * Renders at the top of PropertyDetail with a street-view image + a static
 * block map + external "Open in Google Maps" / "Open Street View" links.
 *
 * Provider auto-selects:
 *   - Google (Street View + Static Maps) when GOOGLE_MAPS_API_KEY is set
 *   - OpenStreetMap (map only, no street view) as the free fallback
 *
 * External Google Maps / Street View links always work regardless of provider
 * (they just deep-link out to google.com and the user sees Google's own view).
 */
export default function PropertyStreetViewCard({ address }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!address) return;
    api.get("/property-map", { params: { address } })
      .then(r => setData(r.data))
      .catch(() => setErr(true));
  }, [address]);

  if (err) return null;
  if (!data) {
    return (
      <div className="mt-4 rugged-card p-6 bg-slate-50 border-2 border-dashed border-slate-300 text-center" data-testid="prop-streetview-loading">
        <MapPin weight="bold" size={22} className="mx-auto text-slate-400"/>
        <div className="mt-1 text-xs font-black uppercase tracking-widest text-slate-500">Locating property…</div>
      </div>
    );
  }
  if (!data.ok || !data.geocoded) {
    return (
      <div className="mt-4 rugged-card p-3 bg-amber-50 border-l-4 border-amber-500 text-sm" data-testid="prop-streetview-nogeo">
        <Warning weight="bold" className="inline text-amber-700 mr-1"/>
        Couldn't auto-locate this address on the map.
        {" "}<a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
          target="_blank" rel="noreferrer"
          className="font-black underline">Search on Google Maps ↗</a>
      </div>
    );
  }

  const hasStreet = !!data.streetview_url;
  return (
    <div className="mt-4 rugged-card overflow-hidden bg-white" data-testid="prop-streetview-card">
      <div className={`grid gap-0 ${hasStreet ? "sm:grid-cols-2" : "sm:grid-cols-1"}`}>
        {hasStreet && (
          <a href={data.google_streetview_link} target="_blank" rel="noreferrer"
             className="relative block group" data-testid="prop-streetview-img">
            <img src={data.streetview_url} alt={`Street view of ${address}`}
                 className="w-full h-56 object-cover bg-slate-200"
                 onError={e => { e.target.style.display = "none"; }}/>
            <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/70 text-white text-[9px] font-black uppercase tracking-widest">
              Street View
            </div>
            <div className="absolute bottom-2 right-2 px-2 py-1 bg-white/95 text-slate-900 text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 transition inline-flex items-center gap-1">
              <ArrowSquareOut weight="bold" size={11}/> Open live view
            </div>
          </a>
        )}
        <a href={data.google_maps_link} target="_blank" rel="noreferrer"
           className="relative block group" data-testid="prop-map-img">
          <img src={data.static_map_url} alt={`Map of ${address}`}
               className="w-full h-56 object-cover bg-slate-200"
               onError={e => { e.target.style.display = "none"; }}/>
          <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-black/70 text-white text-[9px] font-black uppercase tracking-widest">
            {data.provider === "google" ? "Google Maps" : "OpenStreetMap"}
          </div>
          <div className="absolute bottom-2 right-2 px-2 py-1 bg-white/95 text-slate-900 text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 transition inline-flex items-center gap-1">
            <ArrowSquareOut weight="bold" size={11}/> Open in Google Maps
          </div>
        </a>
      </div>
      <div className="px-3 py-2 border-t-2 border-slate-200 bg-slate-50 flex items-center gap-2 flex-wrap text-[10px] font-black uppercase tracking-widest text-slate-600">
        <Compass weight="bold" size={11}/>
        <span>{data.lat?.toFixed(5)}, {data.lng?.toFixed(5)}</span>
        <span className="ml-auto">
          {data.provider === "google"
            ? "Powered by Google"
            : "Free tier · add GOOGLE_MAPS_API_KEY for live Street View"}
        </span>
      </div>
    </div>
  );
}

function Warning({ weight, className }) {
  return <span className={className}>⚠</span>;
}
